import {Injectable, inject} from '@angular/core';
import { Firestore, collection, doc, collectionData, docData, query, where, orderBy, limit, startAfter, getDocs, onSnapshot, QueryDocumentSnapshot, DocumentData, DocumentSnapshot } from '@angular/fire/firestore';
import { User } from '@core/models/user.model';
import { LoadActionLock } from '@core/models/job-file/load-action-lock.model';
import { JpmExpenseLock } from '@core/models/job-file/jpm-expense-lock.model';
import { Subject, combineLatest, Observable, interval, from } from 'rxjs';
import { map, mergeMap, switchMap, startWith, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {

  jobUpdatesCollection = 'JOB_UPDATES';
  private firestore = inject(Firestore);

  getNotifications(orgId: string, jobRefId: string) {
    const date = new Date();
    const orgDoc = doc(this.firestore, orgId, 'JOB_UPDATES');
    const jobUpdatesCollection = collection(orgDoc, jobRefId);
    const q = query(jobUpdatesCollection, where('createdAt', '>=', date));
    
    return new Observable(observer => {
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const changes = snapshot.docChanges().map(change => ({
          type: change.type,
          payload: change.doc
        }));
        observer.next(changes);
      });
      
      return unsubscribe;
    });
  }

  getLiveLocationsOfDifferentOrgVehicles(vehiclesData: Array<{orgId: string, containerId: string}>, unsubscribe: Subject<void>) {
    return vehiclesData.map(v => {
      const orgDoc = doc(this.firestore, v.orgId, 'VEHICLES');
      const locationCollection = collection(orgDoc, 'LOCATION_CHANGES');
      const q = query(locationCollection, where('containerId', '==', v.containerId), limit(1));
      
      return collectionData(q).pipe(takeUntil(unsubscribe));
    });
  }

  getVehiclesLiveLocations(orgId: string, operationId?: string) {
    const orgDoc = doc(this.firestore, orgId, 'VEHICLES');
    const locationCollection = collection(orgDoc, 'LOCATION_CHANGES');
    
    if (!operationId) {
      return new Observable(observer => {
        const unsubscribe = onSnapshot(locationCollection, (snapshot) => {
          const changes = snapshot.docChanges().map(change => ({
            type: change.type,
            payload: change.doc
          }));
          observer.next(changes);
        });
        
        return unsubscribe;
      });
    }

    const q = query(locationCollection, where('operationIds', 'array-contains', operationId));
    return new Observable(observer => {
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const changes = snapshot.docChanges().map(change => ({
          type: change.type,
          payload: change.doc
        }));
        observer.next(changes);
      });
      
      return unsubscribe;
    });
  }

  getVehiclesLiveLocationByIds(orgId: string, vehicleIds: string[]) {
    const chunkSize = 10;
    const vehicleIdChunks = [];
    for (let i = 0; i < vehicleIds.length; i += chunkSize) {
        vehicleIdChunks.push(vehicleIds.slice(i, i + chunkSize));
    }

    const queries = vehicleIdChunks.map(chunk => {
      const orgDoc = doc(this.firestore, orgId, 'VEHICLES');
      const locationCollection = collection(orgDoc, 'LOCATION_CHANGES');
      const q = query(locationCollection, where('id', 'in', chunk));
      return collectionData(q);
    });

    return combineLatest(queries).pipe(
        map(results => [].concat(...results))
    );
  }

  getVehicleLiveLocation(orgId: string, containerId: string) {
    const orgDoc = doc(this.firestore, orgId, 'VEHICLES');
    const locationCollection = collection(orgDoc, 'LOCATION_CHANGES');
    const q = query(locationCollection, where('containerId', '==', containerId));
    return collectionData(q);
  }

  getUnplannedStops(orgId: string, jobRefId: string, jpmId: string) {
    const orgDoc = doc(this.firestore, orgId, this.jobUpdatesCollection);
    const jobUpdatesCollection = collection(orgDoc, jobRefId);
    const q = query(jobUpdatesCollection, where('jpmId', '==', jpmId), where('updatedNotificationType', '==', 'UNPLANNED_STOP'));
    
    return new Observable(observer => {
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const changes = snapshot.docChanges().map(change => ({
          type: change.type,
          payload: change.doc
        }));
        observer.next(changes);
      });
      
      return unsubscribe;
    });
  }

  fetchMoreNotifications(notificationLimit: number, user: User) {
    const userDoc = doc(this.firestore, user.orgId, 'PERSONAL_NOTIFICATIONS');
    const notificationsCollection = collection(userDoc, user.userId);
    const firstQuery = query(notificationsCollection, orderBy('createdAt', 'desc'), limit(notificationLimit));
    
    return new Observable(observer => {
      getDocs(firstQuery).then((documentSnapshots) => {
        if (documentSnapshots.docs.length > 0) {
          const lastOne = documentSnapshots.docs[documentSnapshots.docs.length - 1];
          const nextQuery = query(notificationsCollection, orderBy('createdAt', 'desc'), startAfter(lastOne), limit(notificationLimit));
          
          const unsubscribe = onSnapshot(nextQuery, (snapshot) => {
            const changes = snapshot.docChanges().map(change => ({
              type: change.type,
              payload: change.doc
            }));
            observer.next(changes);
          });
          
          return unsubscribe;
        } else {
          observer.next([]);
        }
      });
    });
  }

  getInitialNotifications(notificationLimit: number, user: User) {
    const userDoc = doc(this.firestore, user.orgId, 'PERSONAL_NOTIFICATIONS');
    const notificationsCollection = collection(userDoc, user.userId);
    const q = query(notificationsCollection, orderBy('createdAt', 'desc'), limit(notificationLimit));
    
    return new Observable(observer => {
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const changes = snapshot.docChanges().map(change => ({
          type: change.type,
          payload: change.doc
        }));
        observer.next(changes);
      });
      
      return unsubscribe;
    });
  }

  getNotificationsByJobRefIds(orgId: string, jobRefIds: string[]) {
    const queries = jobRefIds.map(jobRefId => {
      const orgDoc = doc(this.firestore, orgId, 'JOB_UPDATES');
      const jobUpdatesCollection = collection(orgDoc, jobRefId);
      
      return new Observable(observer => {
        const unsubscribe = onSnapshot(jobUpdatesCollection, (snapshot) => {
          const changes = snapshot.docChanges().map(change => ({
            type: change.type,
            payload: change.doc
          }));
          observer.next(changes);
        });
        
        return unsubscribe;
      });
    });
    
    return combineLatest(queries).pipe(
      map(results => [].concat(...results))
    );
  }

  /**
   * Poll work order notifications every 30 seconds instead of real-time listeners.
   * Reduces persistent Firestore Listen connections.
   */
  getNotificationsByContainerIdsPolling(orgId: string, containerIds: string[], intervalMs = 30000): Observable<Array<{ payload: { doc: { data: () => any } } }>> {
    const fetchNotifications = () => {
      const promises = containerIds.map(containerId => {
        const orgDoc = doc(this.firestore, orgId, 'JOB_UPDATES');
        const jobUpdatesCollection = collection(orgDoc, containerId);
        return getDocs(jobUpdatesCollection);
      });
      return Promise.all(promises).then(snapshots => {
        const results: Array<{ payload: { doc: { data: () => any } } }> = [];
        snapshots.forEach(snapshot => {
          snapshot.docs.forEach(docSnap => {
            results.push({ payload: { doc: docSnap } });
          });
        });
        return results;
      });
    };

    return interval(intervalMs).pipe(
      startWith(0),
      switchMap(() => from(fetchNotifications()))
    );
  }

  /**
   * Listen to notifications at the specified Firestore path
   *
   * @param orgId Organization ID
   * @param notificationType Notification type document
   * @param collectionId Collection ID document
   * @param eventType Event type collection
   * @param eventId Event ID document
   * @param docId Optional specific document ID to listen to
   * @returns Observable of document changes
   */
  listenToNotifications(
    orgId: string,
    notificationType: string,
    collectionId: string,
    eventType: string,
    eventId: string,
    docId?: string
  ): Observable<any> {

    // Build the path step by step
    const orgCollection = collection(this.firestore, orgId);
    const notificationDoc = doc(orgCollection, notificationType);
    const userIdCollection = collection(notificationDoc, 'userId');
    const collectionDoc = doc(userIdCollection, collectionId);
    const eventCollection = collection(collectionDoc, eventType);
    const eventDoc = doc(eventCollection, eventId);
    const notificationsCollection = collection(eventDoc, 'notifications');

    const fullPath = `${orgId}/${notificationType}/userId/${collectionId}/${eventType}/${eventId}/notifications${docId ? `/${docId}` : ''}`;

    if (docId) {
      return docData(doc(notificationsCollection, docId)).pipe(
        tap((data) => {
          if (data) {
            // Handle the data
          }
        })
      );
    } else {
      return collectionData(notificationsCollection).pipe(
        tap()
      );
    }
  }

  /**
   * Live lock state for load-management row actions, one doc per load at
   * `{orgId}/PLANNING_LOAD_ACTION_LOCKS/{jobRefId}/{containerId}`. ms-report writes
   * loadMasterStatus; ms-jobfile writes isScheduled and hasSrOrRfq.
   */
  listenToLoadActionLocks(orgId: string, jobRefId: string): Observable<LoadActionLock[]> {
    const orgDoc = doc(this.firestore, orgId, 'PLANNING_LOAD_ACTION_LOCKS');
    return collectionData(collection(orgDoc, jobRefId)) as Observable<LoadActionLock[]>;
  }

  /**
   * Live invoice states of a JPM, one doc per invoice / payment schedule at
   * `{orgId}/JPM_EXPENSE_LOCKS/{jpmId}/{invoiceId}`. ms-invoice writes it whenever an
   * invoice holding the JPM is created, changes state or gains / loses the JPM's load.
   */
  listenToJpmExpenseLocks(orgId: string, jpmId: string): Observable<JpmExpenseLock[]> {
    const orgDoc = doc(this.firestore, orgId, 'JPM_EXPENSE_LOCKS');
    return collectionData(collection(orgDoc, jpmId)) as Observable<JpmExpenseLock[]>;
  }
}
